import React from 'react';
import { ListItem, ListItemIcon, ListItemText, Chip, Typography, Box } from '@mui/material';
import {
  CheckCircle as IncludeIcon,
  Cancel as ExcludeIcon,
  Comment as CommentIcon,
  Block as CommentOffIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { AuditTrailEntryDTO, AuditAction } from '../dtos';
import { QuestionWithResponsesForAudit } from '../types';
import { useUserDisplayName } from '../hooks/useEntityDisplay';

interface AuditTrailEntryProps {
  entry: AuditTrailEntryDTO;
  questions: QuestionWithResponsesForAudit[];
}

export const AuditTrailEntry: React.FC<AuditTrailEntryProps> = ({ entry, questions }) => {
  const { displayName: userDisplayName } = useUserDisplayName(entry.userId);

  // Find the question and response directly from the loaded questions data
  const question = questions.find((q) => q.questionId === entry.questionId);
  const questionDisplayText =
    question?.questionText || `Question ${entry.questionId?.substring(0, 8) ?? 'Unknown'}...`;

  // Find the specific response to show its value
  const response = question?.responses.find((r) => r.id === entry.responseId);

  const formatResponseValue = (value: string | number | string[]): string => {
    if (!value) return 'No response';

    // Handle arrays (multiple choice)
    if (Array.isArray(value)) {
      return value.map((v) => getOptionLabel(v)).join(', ');
    }

    // Handle boolean values for Yes/No questions
    if (value === 'true') return 'Yes';
    if (value === 'false') return 'No';

    // Try to map option ID to label if question has options
    if (question?.options && question.options.length > 0) {
      return getOptionLabel(value);
    }

    return String(value);
  };

  const getOptionLabel = (optionId: string | number): string => {
    if (!question?.options) return String(optionId);

    const option = question.options.find((opt: any) => opt.id === String(optionId));
    if (option?.label) {
      // Handle multilingual labels
      if (typeof option.label === 'object') {
        return (
          option.label.en ||
          option.label.no ||
          option.label.de ||
          Object.values(option.label)[0] ||
          String(optionId)
        );
      }
      return option.label;
    }
    return String(optionId);
  };

  const responseDisplayText = response
    ? `"${formatResponseValue(response.responseValue).substring(0, 30)}${formatResponseValue(response.responseValue).length > 30 ? '...' : ''}"`
    : `Response ${entry.responseId?.substring(0, 8) ?? 'Unknown'}...`;

  const getActionIcon = (action: AuditAction) => {
    switch (action) {
      case 'RESPONSE_INCLUDED':
        return <IncludeIcon color="success" />;
      case 'RESPONSE_EXCLUDED':
        return <ExcludeIcon color="error" />;
      case 'COMMENT_INCLUDED':
        return <CommentIcon color="info" />;
      case 'COMMENT_EXCLUDED':
        return <CommentOffIcon color="warning" />;
      default:
        return <TimeIcon />;
    }
  };

  const getActionLabel = (action: AuditAction) => {
    switch (action) {
      case 'RESPONSE_INCLUDED':
        return 'Response Included';
      case 'RESPONSE_EXCLUDED':
        return 'Response Excluded';
      case 'COMMENT_INCLUDED':
        return 'Comment Included';
      case 'COMMENT_EXCLUDED':
        return 'Comment Excluded';
      default:
        return action;
    }
  };

  const getActionColor = (action: AuditAction) => {
    switch (action) {
      case 'RESPONSE_INCLUDED':
      case 'COMMENT_INCLUDED':
        return 'success';
      case 'RESPONSE_EXCLUDED':
      case 'COMMENT_EXCLUDED':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString(),
    };
  };

  const timestamp = formatTimestamp(entry.timestamp);

  return (
    <ListItem
      sx={{
        py: 2,
        alignItems: 'flex-start',
      }}
    >
      <ListItemIcon sx={{ mt: 0.5 }}>{getActionIcon(entry.action)}</ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip
              label={getActionLabel(entry.action)}
              color={getActionColor(entry.action)}
              size="small"
              variant="outlined"
            />
            <Typography variant="body2" color="text.secondary">
              {timestamp.date} at {timestamp.time}
            </Typography>
          </Box>
        }
        secondary={
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="body2">
                <strong>Question:</strong> {questionDisplayText}
              </Typography>
              <Typography variant="body2">
                <strong>Response:</strong> {responseDisplayText}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {userDisplayName}
              </Typography>
            </Box>
          </Box>
        }
      />
    </ListItem>
  );
};
